import { GoChevronLeft, GoChevronRight } from 'react-icons/go';
import React, { createContext, useContext, useRef, useCallback } from 'react';

// Create a context for the table container
const TableContainerContext = createContext<{
    ref: React.RefObject<HTMLDivElement>;
    resetScroll: () => void; // Function to reset scroll position
} | null>(null);

// Provider component to wrap the table and buttons
export const TableContainerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);

    // Function to reset scroll position to 0
    const resetScroll = useCallback(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTo({
                left: 0,
                behavior: 'smooth', // Smooth scrolling
            });
        }
    }, []);

    return (
        <TableContainerContext.Provider value={{ ref: tableContainerRef, resetScroll }}>
            {children}
        </TableContainerContext.Provider>
    );
};

// Custom hook to access the table container ref and reset function
export const useTableContainer = () => {
    const context = useContext(TableContainerContext);
    if (!context) {
        throw new Error('useTableContainer must be used within a TableContainerProvider');
    }
    return context;
};

// Updated TableScrollerButtons component
export const TableScrollerButtons: React.FC<{
    containerProps?: React.HTMLAttributes<HTMLDivElement>;
}> = ({ containerProps }) => {
    const scrollAmount = 700; // Adjust this value to control how much to scroll
    const parentContainer = useTableContainer();

    const handleScrollLeft = () => {
        if (parentContainer.ref.current) {
            parentContainer.ref.current.scrollBy({
                left: -parentContainer.ref.current.scrollWidth,
                behavior: 'smooth', // Smooth scrolling
            });
        }
    };

    const handleScrollRight = () => {
        if (parentContainer.ref.current) {
            parentContainer.ref.current.scrollBy({
                left: parentContainer.ref.current.scrollWidth,
                behavior: 'smooth', // Smooth scrolling
            });
        }
    };

    return (
        <div
            {...containerProps}
            className={`flex justify-end items-center space-x-2 ${containerProps?.className || ''}`}
        >
            <button
                onClick={handleScrollLeft}
                className={`active:opacity-60 py-1 px-1 bg-[#EFF0F0] text-[#4F5E71] rounded-[4px]`}
            >
                <GoChevronLeft className="text-lg" />
            </button>

            <button
                onClick={handleScrollRight}
                className={`active:opacity-60 py-1 px-1 bg-[#EFF0F0] text-[#4F5E71] rounded-[4px]`}
            >
                <GoChevronRight className="text-lg" />
            </button>
        </div>
    );
};